import axios from "axios"
import { API_ROUTER } from "."
import { IncomingMessage } from "http"
import { decodeJwt, JWTPayload } from "jose"
import { Session } from "next-auth"
import { getToken, JWT } from "next-auth/jwt"
import { NextApiRequestCookies } from "next/dist/server/api-utils"
import { authDomain, userPoolWebClientId } from "@web/config/next"
import { CognitoRefreshTokenResult } from "platform-common/domain/auth/cognito"

export type SsrRequest = IncomingMessage & {
  cookies: NextApiRequestCookies
}

/**
 * Get current session on the server.
 */
export const getSsrSession = (req: SsrRequest): Promise<Session | null> => getToken({ req }) as Promise<Session | null>

/**
 * Get access token on the server, refreshing if needed.
 */
export const getSsrFreshAccessToken = async (req: SsrRequest): Promise<string | null> => {
  const session = await getSsrSession(req)
  // if we have an access token and it is expired, get a fresh one
  let accessToken = session?.accessToken
  if (session && accessToken && isAccessTokenExpired(accessToken)) {
    const newToken = await refreshCognitoAccessToken(session)
    accessToken = newToken.access_token
  }
  return accessToken ?? null
}
type DecodedToken = JWTPayload & { username: string }
export const getSsrFreshAccessDecodedToken = async (req: SsrRequest): Promise<DecodedToken | null> => {
  const token = await getSsrFreshAccessToken(req)
  if (!token) return null
  return decodeJwt(token) as DecodedToken
}

export const getFreshAccessToken = async (session?: Session): Promise<string | null> => {
  if (!session) return null
  const refreshedSession = await refreshTokensIfNeeded(session)
  return refreshedSession?.accessToken ?? null
}

export const isSessionExpired = (session: Session): boolean => isAccessTokenExpired(session.accessToken)

export const refreshTokensIfNeeded = async (session: Session): Promise<Session> => {
  // return current token if the access token has not expired yet
  if (!isSessionExpired(session)) return session

  // access token has expired, try to get a new one
  const refreshedTokens = await refreshCognitoAccessToken(session)

  // return session with updated tokens
  return {
    ...session,
    accessToken: refreshedTokens?.access_token || session.accessToken,
    refreshToken: refreshedTokens?.refresh_token || session.refreshToken, // Fall back to old refresh token
    idToken: refreshedTokens?.id_token || session.idToken, // session.idToken won't ever be set

    accessTokenExpires: refreshedTokens?.expires_in
      ? Date.now() + Number(refreshedTokens?.expires_in) * 1000
      : session.accessTokenExpires,
  }
}

/**
 * Decode JWT and check expiration time.
 * @param accessTokenEncoded encoded JWT string
 */
export const isAccessTokenExpired = (accessTokenEncoded: string): boolean => {
  const accessTokenDecoded = decodeJwt(accessTokenEncoded)
  if (!accessTokenDecoded) return true
  if (!accessTokenDecoded.exp) throw new Error("access token does not have an expiration")

  let expiresIn = accessTokenDecoded.exp - Date.now() / 1000 // seconds until expiration
  // 10s buffer for clock skew etc
  expiresIn -= 10

  if (expiresIn < 0) console.debug("access token expired ", expiresIn * -1, " seconds ago")

  return expiresIn < 0
}

export const refreshCognitoAccessToken = async (tokens: JWT): Promise<CognitoRefreshTokenResult> => {
  if (!userPoolWebClientId) throw new Error("COGNITO_CLIENT_ID is not set")
  if (!authDomain) throw new Error("COGNITO_DOMAIN_NAME is not set")

  const res = await fetch(`https://${authDomain}/oauth2/token`, {
    method: "POST",
    headers: new Headers({ "content-type": "application/x-www-form-urlencoded" }),
    body: Object.entries({
      grant_type: "refresh_token",
      client_id: userPoolWebClientId,
      refresh_token: tokens.refreshToken,
    })
      .map(([k, v]) => `${k}=${v}`)
      .join("&"),
  })
  if (!res.ok) {
    throw new Error(JSON.stringify(await res.json()))
  }
  const newTokens = (await res.json()) as CognitoRefreshTokenResult
  return newTokens
}

export const postAuthenticationNotificationToSlack = async (url?: string) =>
  axios.post(
    API_ROUTER.sendUserAuthenticatedSlackNotification(),
    { url },
    { headers: { Authorization: `Bearer ${await getFreshAccessToken()}` } }
  )
